import { useAuth } from '../context/auth-context';
import React, { useEffect, useState } from 'react';
import { Modal, Box, Tab, Tabs, TextField, Button } from '@mui/material';
import FormGenerator from './formGenerator';
import { toast } from "react-toastify";
import axios from 'axios';
import { rootUrl } from '../helpers';
import Select from 'react-select';

const LoginSignupModal = ({ open, handleClose }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    isValid: false
  });
  const [signupData, setSignupData] = useState({
    councilId: '',
    firstName: '',
    otherName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    nrc: '',
    dateOfBirth: '',
    gender: '',
    password: '',
    confirmPassword: ''
  });
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const auth = useAuth()

  const handleTabChange = (newValue) => {
    setTabIndex(newValue);
  };

  const [councils, setCouncil] = useState([]);

  // Password validation function
  const validatePassword = (password) => {
    const feedback = [];
    let score = 0;

    // Check minimum length
    if (password.length < 8) {
      feedback.push("Password must be at least 8 characters long");
    } else {
      score += 1;
    }

    // Check for uppercase letters
    if (!/[A-Z]/.test(password)) {
      feedback.push("Password must contain at least one uppercase letter");
    } else {
      score += 1;
    }

    // Check for lowercase letters
    if (!/[a-z]/.test(password)) {
      feedback.push("Password must contain at least one lowercase letter");
    } else {
      score += 1;
    }

    // Check for numbers
    if (!/\d/.test(password)) {
      feedback.push("Password must contain at least one number");
    } else {
      score += 1;
    }

    // Check for special characters
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      feedback.push("Password must contain at least one special character (!@#$%^&*...)");
    } else {
      score += 1;
    }

    // Check for common patterns to avoid
    if (/(.)\1{2,}/.test(password)) {
      feedback.push("Password should not contain repeated characters (e.g., 'aaa')");
    } else {
      score += 1;
    }

    // Check for common words
    const commonWords = ['password', '123456', 'qwerty', 'admin', 'user'];
    if (commonWords.some(word => password.toLowerCase().includes(word))) {
      feedback.push("Password should not contain common words");
    } else {
      score += 1;
    }

    const isValid = score >= 5 && password.length >= 8;

    return {
      score,
      feedback,
      isValid
    };
  };

  const fetchData = async () => {
    try {
      const data = await axios.get(rootUrl(`/councils`));
      if (data.status === 200) {
        setCouncil(data.data.data);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  useEffect(()=>{
    fetchData()
  }, [])

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    auth.login(loginData, () => {
      handleClose()
      window.location.reload()
    })
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    // Password validation
    if (!passwordStrength.isValid) {
      toast.error('Please ensure your password meets all security requirements');
      return;
    }

    if(signupData.password !== signupData.confirmPassword){
      toast.error('Passwords do not match!')
      return
    }
      
    try{
        const response = await axios.post(rootUrl('/users'), signupData)
        if(response.data.status == 200){
            toast.success('Account created successfully!')
            setTabIndex(0)
            setSignupData({
              councilId: '',
              firstName: '',
              otherName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
              nrc: '',
              dateOfBirth: '',
              gender: '',
              password: '',
              confirmPassword: ''
            });
        }else{
          toast.error(response.data.message)
        }
    }catch(e){
        toast.error(e?.message)
    }
  };

  const handleSignupChange = (field, value) => {
    setSignupData(prev => ({
      ...prev,
      [field]: value
    }));

    // Update password strength when password changes
    if (field === 'password') {
      const validation = validatePassword(value);
      setPasswordStrength(validation);
    }
  };

  const handleLoginChange = (field, value) => {
    setLoginData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const genderOptions = [
    { value: '', label: 'Select gender' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' }
  ];

  const councilOptions = councils?.map(res => ({
    value: res.code,
    label: res.name,
  })) || [];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className=''
    >
      <Box className='bg-white mt-20 h-[90vh] fixed w-full bottom-0 rounded-t-3xl overflow-y-auto'>
        <div className='flex justify-center p-5 m-5'>
        {tabIndex === 0 && (
           <div className="w-full max-w-md">
           <div className='font-semi-bold text-center border-bottom'>
               <span className='text-3xl'> Gramosi</span>
               <p className='text-lg'> Welcome, Please login to your account. </p>
           </div>
           <hr className="border-b-gray-200 p-3 m-3"></hr>
           
           {/* Custom Login Form */}
           <form onSubmit={handleLoginSubmit} className="space-y-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
               <input
                 type="email"
                 value={loginData.email}
                 onChange={(e) => handleLoginChange('email', e.target.value)}
                 className="w-full p-2 border border-gray-300 rounded-md"
                 placeholder="Enter your email"
                 required
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
               <input
                 type="password"
                 value={loginData.password}
                 onChange={(e) => handleLoginChange('password', e.target.value)}
                 className="w-full p-2 border border-gray-300 rounded-md"
                 placeholder="Enter your password"
                 required
               />
             </div>

             <button
               type="submit"
               className="w-full bg-blue-950 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-950 focus:ring-opacity-50"
             >
               Login
             </button>
           </form>
           
           {/* Add Forgot Password here */}
           <div className='text-center mt-4 text-sm'>
               <span 
                   onClick={() => handleTabChange(2)} 
                   className='text-blue-500 hover:cursor-pointer hover:underline'
               >
                   Forgot Password?
               </span>
           </div>
           
           <div className='text-center m-5'>
               Don't have an account? {' '}
               <span 
                   onClick={() => handleTabChange(1)} 
                   className='text-blue-500 hover:cursor-pointer hover:underline'
               >
                   Signup
               </span>
           </div>
       </div>
        )}
        {tabIndex === 1 && (
            <div className="w-full max-w-md">
                 <div className='font-semi-bold text-center border-bottom'>
                <span className='text-3xl'> Gramosi </span>
                <p className='text-lg'> Welcome, Please create an account. </p>
            </div>
            <hr className="border-b-gray-200 p-3 m-3"></hr>
            
            {/* Password Requirements Info */}
            {/* <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-semibold text-blue-800 mb-2">Password Requirements:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• At least 8 characters long</li>
                <li>• At least one uppercase letter (A-Z)</li>
                <li>• At least one lowercase letter (a-z)</li>
                <li>• At least one number (0-9)</li>
                <li>• At least one special character (!@#$%^&*...)</li>
                <li>• No repeated characters (e.g., 'aaa')</li>
                <li>• No common words (password, 123456, etc.)</li>
              </ul>
            </div>
             */}
            {/* Custom Signup Form */}
            <form onSubmit={handleSignupSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Council *</label>
                <Select
                  options={councilOptions}
                  value={councilOptions.find(option => option.value === signupData.councilId)}
                  onChange={(selected) => handleSignupChange('councilId', selected?.value || '')}
                  placeholder="Select Council"
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={signupData.firstName}
                    onChange={(e) => handleSignupChange('firstName', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Name</label>
                  <input
                    type="text"
                    value={signupData.otherName}
                    onChange={(e) => handleSignupChange('otherName', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={signupData.lastName}
                  onChange={(e) => handleSignupChange('lastName', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={signupData.email}
                  onChange={(e) => handleSignupChange('email', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="text"
                  value={signupData.phoneNumber}
                  onChange={(e) => handleSignupChange('phoneNumber', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NRC *</label>
                <input
                  type="text"
                  value={signupData.nrc}
                  onChange={(e) => handleSignupChange('nrc', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                <input
                  type="date"
                  value={signupData.dateOfBirth}
                  onChange={(e) => handleSignupChange('dateOfBirth', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <Select
                  options={genderOptions}
                  value={genderOptions.find(option => option.value === signupData.gender)}
                  onChange={(selected) => handleSignupChange('gender', selected?.value || '')}
                  placeholder="Select Gender"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input
                  type="password"
                  value={signupData.password}
                  onChange={(e) => handleSignupChange('password', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <input
                  type="password"
                  value={signupData.confirmPassword}
                  onChange={(e) => handleSignupChange('confirmPassword', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              {/* Password Strength Indicator */}
              {signupData.password && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium mr-2">Password Strength:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength.score <= 2 ? 'bg-red-500' :
                          passwordStrength.score <= 4 ? 'bg-yellow-500' :
                          passwordStrength.score <= 6 ? 'bg-blue-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${(passwordStrength.score / 7) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs ml-2">
                      {passwordStrength.score <= 2 ? 'Weak' :
                       passwordStrength.score <= 4 ? 'Fair' :
                       passwordStrength.score <= 6 ? 'Good' : 'Strong'}
                    </span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="text-xs text-gray-600">
                      <div className="font-medium mb-1">Missing Requirements:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {passwordStrength.feedback.map((item, index) => (
                          <li key={index} className="text-red-500">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {passwordStrength.isValid && (
                    <div className="text-xs text-blue-600 font-medium">
                      ✓ Password meets all security requirements
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-blue-950 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-950 focus:ring-opacity-50"
              >
                Create Account
              </button>
            </form>
             
             <div className='text-center m-5'>Already have an account?  <span onClick={()=>handleTabChange(0)} className='text-blue-500'>login</span> </div>
            </div>
        )}
        {tabIndex === 2 && (
            <ForgotPassword handleTabChange={handleTabChange} /> 
        )}
        </div>
      </Box>
    </Modal>

  );
};


function normalizePhoneNumber(phone) {
  if (!phone) return phone;

  phone = phone.trim().replace(/[\s-]/g, '');
  if (phone.startsWith('+260')) {
    return phone.slice(1);
  }
  if (phone.startsWith('260')) {
    return phone;
  }
  if (phone.startsWith('09')) {
    return '260' + phone.slice(1);
  }
  if (phone.startsWith('9')) {
    return '260' + phone;
  }

  return phone;
}





function ForgotPassword({ handleTabChange }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Loading state

  function normalizePhoneNumber(phone) {
    if (!phone) return phone;
    phone = phone.trim().replace(/[\s-]/g, "");
    if (phone.startsWith("+260")) return phone.slice(1);
    if (phone.startsWith("260")) return phone;
    if (phone.startsWith("09")) return "260" + phone.slice(1);
    if (phone.startsWith("9")) return "260" + phone;
    return phone;
  }

  const handleEmailSubmit = async () => {
    setError("");
    setIsLoading(true); // Start loading

    try {
      const response = await axios.get(rootUrl(`/forgotPassword/${email}`));

      if (response.data.message === "Email is sent!") {
        const normalizedPhone = normalizePhoneNumber(response.data.data.phone);
        setPhone(normalizedPhone);
        localStorage.setItem("verificationCode", response.data.data.code);
        setStep(2);
      } else {
        toast.error("Failed to send verification code");
      }
    } catch (err) {
      setError(err?.message);
      toast.error(err?.message);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleCodeSubmit = () => {
    setError("");
    const storedCode = localStorage.getItem("verificationCode");
    if (code === storedCode) {
      setStep(3);
    } else {
      setError("Incorrect verification code. Please try again.");
    }
  };

  const handlePasswordSubmit = async () => {
    setError("");
    try {
      
      await axios.post(rootUrl(`/updatePassword`), { email:email,password:newPassword });
      localStorage.removeItem("verificationCode");
      handleTabChange(0);
    } catch (err) {
      setError("Failed to update password. Please try again.");
    }
  };

  return (
    <div className="max-w-md mx-auto p-4">
      <div className="font-semibold text-center pb-2 mb-4">
        <span className="text-3xl">Gramosi</span>
        <p className="text-lg">Forgot Password</p>
      </div>
      <hr className="border-b-gray-200 my-3" />

      {step === 1 && (
        <div>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border p-2 rounded w-full mb-3"
            disabled={isLoading} // Disable input when loading
          />
          <button
            onClick={handleEmailSubmit}
            className={`w-full p-2 rounded ${
              isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 text-white"
            }`}
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send Verification Code"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className="mb-3 text-center">
            A verification code has been sent to your email and phone ending in{" "}
            <strong>{phone.slice(-4)}</strong>.
          </p>
          <input
            type="text"
            placeholder="Enter verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="border p-2 rounded w-full mb-3"
          />
          <button
            onClick={handleCodeSubmit}
            className="w-full p-2 bg-blue-500 text-white rounded"
          >
            Verify Code
          </button>
        </div>
      )}

      {step === 3 && (
        <div>
          <input
            type="password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="border p-2 rounded w-full mb-3"
          />
          <button
            onClick={handlePasswordSubmit}
            className="w-full p-2 bg-blue-500 text-white rounded"
          >
            Update Password
          </button>
        </div>
      )}

      {error && <p className="text-red-500 text-center mt-3">{error}</p>}

      <div className="text-center mt-5">
        Already have an account?{" "}
        <span onClick={() => handleTabChange(0)} className="text-blue-500 cursor-pointer">
          Login
        </span>
      </div>
    </div>
  );
}




export default LoginSignupModal;
